import { Box, Flex, Heading } from '@radix-ui/themes';
import { NotifyHelper } from 'classes/helpers/notify.helper';
import { PitchListHelper } from 'classes/helpers/pitch-list.helper';
import { AssignPitcherDialog } from 'components/common/dialogs/assign-pitcher';
import { ErrorBoundary } from 'components/common/error-boundary';
import { CommonTableCheckedMenu } from 'components/common/table/checked-menu';
import usePitchListStore from 'components/sections/pitch-list/store/use-pitch-list-store';
import { CheckedContext } from 'contexts/layout/checked.context';
import { MachineContext } from 'contexts/machine.context';
import { PitchListDialog } from 'enums/pitch-lists';
import { t } from 'i18next';
import { TrainPitchesFn } from 'interfaces/function-types';
import { IAssignPitcher } from 'interfaces/i-assign-pitcher';
import { IMenuAction } from 'interfaces/i-menus';
import { ArrayHelper } from 'lib_ts/classes/array.helper';
import { MiscHelper } from 'lib_ts/classes/misc.helper';
import { RADIX } from 'lib_ts/enums/radix-ui';
import { clone, first } from 'lodash';
import { useContext, useMemo, useState } from 'react';
import { MainService } from 'services/main.service';
import slugify from 'slugify';
import { useAuthStore } from 'stores/use-auth-store';
import useListLibraryStore from 'stores/use-list-library-store';
import { useTrainedMsStore } from 'stores/use-trained-ms-store';
import { useVideosStore } from 'stores/use-videos-store';

export const PitchListFooter = (props: { onTrainFn: TrainPitchesFn }) => {
  const readyToTrain = useTrainedMsStore((s) => s.readyToTrain);

  const activeList = useListLibraryStore((s) => s.activeList);
  const activeReadOnly = useListLibraryStore((s) => s.isActiveListReadOnly());

  const filteredPitches = usePitchListStore((s) => s.filteredPitches);

  const isInGame = useAuthStore((s) => s.isInGame);

  const { machine } = useContext(MachineContext);

  const { getChecked } = useContext(CheckedContext);

  const checkedPitches = useMemo(
    () => filteredPitches.filter((p) => getChecked(p._id)),
    [filteredPitches, getChecked]
  );

  const [dialogAssignPitcher, setDialogAssignPitcher] = useState<{
    key: number;
    value: IAssignPitcher;
  }>();

  const checkedActions = useMemo(() => {
    if (checkedPitches.length === 0) {
      return [];
    }

    const trainedChecked = checkedPitches.filter(
      (p) => p._mx_tms?._mx_trained && !p.debugMs // only reset trained non-debug pitches
    );

    const output: IMenuAction[] = [
      {
        label: 'common.train-pitches',
        disabled: !readyToTrain,
        color: RADIX.COLOR.TRAIN_PITCH,
        onClick: () => props.onTrainFn(checkedPitches.map((p) => p._id)),
      },
      {
        label: 'common.assign-pitcher',
        invisible: activeReadOnly,
        onClick: () => {
          const firstChecked = first(checkedPitches);

          setDialogAssignPitcher({
            key: Date.now(),
            value: {
              mlb: firstChecked?.mlb_repertoire ?? null,
              name: firstChecked?.pitcher ?? null,
              team: firstChecked?.team ?? null,
              teamPk: firstChecked?.teamPk ?? null,
            },
          });
        },
      },
      {
        label: 'pl.change-videos',
        invisible: activeReadOnly,
        onClick: () =>
          usePitchListStore.getState().openDialog({
            dialog: PitchListDialog.ChangeVideo,
            pitches: checkedPitches,
          }),
      },
      {
        label: 'pl.duplicate-pitches',
        invisible: isInGame,
        onClick: () => {
          usePitchListStore.getState().openDialog({
            dialog: PitchListDialog.Copy,
            pitches: checkedPitches.map((p) => clone(p)),
          });
        },
      },
      {
        label: 'pl.export-pitches',
        invisible: isInGame,
        onClick: async () => {
          if (!activeList) {
            NotifyHelper.warning({
              message_md: t('pl.no-active-pitch-list'),
            });
            return;
          }

          const videosDict = ArrayHelper.firstBy(
            useVideosStore.getState().videos,
            '_id'
          );

          const payload = checkedPitches.map((p) =>
            PitchListHelper.convertToCustomExport({
              pitch: p,
              videosDict: videosDict,
              plate_ft: machine.releaseFt,
            })
          );

          // SessionEventsService.createOne({
          //   category: 'pitch',
          //   tags: 'export',
          //   data: {
          //     event: 'list (checked) to CSV',
          //     list_id: activeList._id,
          //     list_name: activeList.name,
          //     count: payload.length,
          //   },
          // });

          const csvString =
            await MainService.getInstance().convertJSONToCSV(payload);

          const blob = new Blob([csvString], { type: 'text/csv' });
          MiscHelper.saveAs(blob, `${slugify(activeList.name)}-checked.csv`);
        },
      },
      {
        label: 'common.reset-training-data',
        disabled: !readyToTrain,
        invisible: activeReadOnly || trainedChecked.length === 0,
        onClick: () =>
          usePitchListStore.getState().openDialog({
            dialog: PitchListDialog.Reset,
            pitches: checkedPitches,
          }),
        color: RADIX.COLOR.WARNING,
      },
      {
        label: 'pl.delete-pitches',
        invisible: activeReadOnly || isInGame,
        onClick: () =>
          usePitchListStore.getState().openDialog({
            dialog: PitchListDialog.DeletePitches,
            pitches: checkedPitches,
          }),
        color: RADIX.COLOR.DANGER,
      },
    ];

    return output.filter((m) => !m.invisible);
  }, [
    activeReadOnly,
    readyToTrain,
    checkedPitches,
    machine.last_hardware_changed,
    props.onTrainFn,
    isInGame,
  ]);

  return (
    <ErrorBoundary identifier="PitchListFooter">
      {checkedActions.length > 0 && (
        <Flex flexGrow="1" gap={RADIX.FLEX.GAP.SM}>
          <Heading size={RADIX.HEADING.SIZE.MD} mb="1">
            {checkedPitches.length}&nbsp;
            {t('common.selected').toLowerCase()}
          </Heading>
          <Box ml="1" className="valign-center">
            <CommonTableCheckedMenu actions={checkedActions} />
          </Box>
        </Flex>
      )}

      {dialogAssignPitcher && (
        <AssignPitcherDialog
          key={dialogAssignPitcher.key}
          defaultValue={dialogAssignPitcher.value}
          onChange={(value) => {
            usePitchListStore.getState().updatePitches({
              payloads: checkedPitches.map((p) => ({
                _id: p._id,
                mlb_repertoire: value.mlb,
                pitcher: value.name,
                team: value.team,
                teamPk: value.teamPk,
              })),
            });
          }}
          onClose={() => setDialogAssignPitcher(undefined)}
        />
      )}
    </ErrorBoundary>
  );
};
